import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { FiChevronRight, FiClipboard, FiExternalLink } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { allows, unitsOf } from '../../lib/permissions';
import { formatDate } from '../../lib/format';
import { Notice, Panel, Pill, SkeletonRows } from '../ui/kit';
import DocumentLinks from '../documents/DocumentLinks';
import DocumentViewer from '../documents/DocumentViewer';

const TYPES = ['Normal', 'Mission Canadienne', 'Mission Suisse'];
const typeLabel = (item) => (TYPES.includes(item.commentType) ? item.commentType : item.comment || 'Autre');

// Staff documents that mention this patient (field patientIds, set in the Staff section).
const PatientStaff = ({ patient, user, rights }) => {
  const [state, setState] = useState({ status: 'loading', items: [] });
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getDocs(query(collection(db, 'filesObs'), where('patientIds', 'array-contains', patient._id)))
      .then((snapshot) => {
        const items = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
        if (!cancelled) setState({ status: 'ready', items });
      })
      .catch(() => !cancelled && setState({ status: 'error', items: [] }));
    return () => {
      cancelled = true;
    };
  }, [patient._id]);

  return (
    <Panel
      title="Staff"
      action={
        <Link to="/staff" className="inline-flex items-center gap-1 text-[13px] font-medium text-brand-600 no-underline transition hover:text-brand-700 print:hidden">
          Section Staff <FiChevronRight size={13} aria-hidden="true" />
        </Link>
      }
    >
      {state.status === 'loading' ? (
        <SkeletonRows rows={1} />
      ) : state.status === 'error' ? (
        <Notice tone="amber">Documents de staff indisponibles pour le moment.</Notice>
      ) : state.items.length === 0 ? (
        <p className="m-0 text-[13.5px] text-black/45">Ce patient n’est mentionné dans aucun document de staff.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-[12px] p-0">
          {state.items.map((item) => (
            <li key={item.id} className="flex gap-[12px] rounded-2xl bg-mist px-[14px] py-[12px]">
              <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-white text-teal-700">
                <FiClipboard size={15} aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[14px] font-semibold text-ink">{formatDate(item.timestamp) || 'Date non précisée'}</span>
                  <Pill tone={typeLabel(item) === 'Normal' ? 'neutral' : 'brand'}>{typeLabel(item)}</Pill>
                </div>
                {item.document && item.document.id ? (
                  <div className="mt-1">
                    <DocumentLinks documents={[item.document]} onOpen={() => setViewing(item)} size="sm" />
                  </div>
                ) : item.link ? (
                  <a href={item.link} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 break-all text-[13px] font-medium text-brand-600 no-underline hover:text-brand-700">
                    <FiExternalLink size={12} aria-hidden="true" className="flex-none" />
                    {item.name || 'Document'}
                  </a>
                ) : (
                  <p className="mb-0 mt-1 text-[13px] text-black/55">{item.name}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {viewing && (
        <DocumentViewer
          item={viewing.document}
          onClose={() => setViewing(null)}
          canReplace={allows(rights, 'staff', 'modifier') && (user.role === 'Admin' || unitsOf(viewing.service).some((unit) => unitsOf(user.service).includes(unit)))}
          canDownload={allows(rights, 'staff', 'tout')}
        />
      )}
    </Panel>
  );
};

export default PatientStaff;
